import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'

interface AdminStats {
    totalUsers: number; totalAgents: number; totalAdmins: number; totalCustomers: number;
    totalProperties: number; activeProperties: number; forRent: number; forSale: number;
    totalBookings: number;
    totalLocalities: number;
    highGrowthLocalities: number;
    pendingProjectInquiries?: number;
    pendingLocalityInquiries?: number;
    pendingCommonInquiries?: number;
    newLeads?: number;
    pendingSubscriptions?: number;
    pendingBookings?: number;
}

export default function AdminDashboard() {
    const { user, token } = useAuth()
    const [stats, setStats] = useState<AdminStats | null>(null)
    const [loading, setLoading] = useState(true)
    const [clearedBadges, setClearedBadges] = useState<string[]>(() => {
        try { return JSON.parse(localStorage.getItem('clearedBadges') || '[]'); } catch { return []; }
    });

    const handleActionClick = (path: string) => {
        if (!clearedBadges.includes(path)) {
            const newCleared = [...clearedBadges, path];
            setClearedBadges(newCleared);
            localStorage.setItem('clearedBadges', JSON.stringify(newCleared));
        }
    };

    useEffect(() => {
        if (token) {
            api.adminGetStats(token)
                .then(data => { if (!data.message) setStats(data) })
                .catch(console.error)
                .finally(() => setLoading(false))
        } else { setLoading(false) }
    }, [token])

    const statCards = stats ? [
        { label: 'Total Users', value: stats.totalUsers, icon: 'fa-users', color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20', sub: `${stats.totalAgents} agents · ${stats.totalCustomers} customers` },
        { label: 'Inventory', value: stats.totalProperties, icon: 'fa-building', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20', sub: `${stats.activeProperties} active listings` },
        { label: 'Site Visits', value: stats.totalBookings, icon: 'fa-calendar-check', color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20', sub: 'Pending inquiries' },
        { label: 'Market Trends', value: stats.totalLocalities, icon: 'fa-map-location-dot', color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20', sub: `${stats.highGrowthLocalities} high-growth areas` },
    ] : []

    const allQuickActions = [
        { label: 'Project Inq', path: '/admin-project-inquiries', icon: 'fa-building-circle-check', color: 'text-indigo-600', bg: 'bg-indigo-50 dark:bg-indigo-900/20', perm: 'Inquiry Portal', badge: stats?.pendingProjectInquiries || 0 },
        { label: 'Locality Inq', path: '/admin-locality-inquiries', icon: 'fa-map-location', color: 'text-sky-600', bg: 'bg-sky-50 dark:bg-sky-900/20', perm: 'Inquiry Portal', badge: stats?.pendingLocalityInquiries || 0 },
        { label: 'Common Inq', path: '/admin-common-inquiries', icon: 'fa-comment-dots', color: 'text-rose-600', bg: 'bg-rose-50 dark:bg-rose-900/20', perm: 'Inquiry Portal', badge: stats?.pendingCommonInquiries || 0 },
        { label: 'Leads CRM', path: '/admin-leads', icon: 'fa-headset', color: 'text-violet-600', bg: 'bg-violet-50 dark:bg-violet-900/20', perm: 'CRM Leads', badge: stats?.newLeads || 0 },
        { label: 'Localities', path: '/admin-localities', icon: 'fa-map-location-dot', color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20', perm: 'Localities' },
        { label: 'Developers', path: '/admin-developers', icon: 'fa-building-user', color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20', perm: 'Developers' },
        { label: 'Inventory', path: '/admin-inventory', icon: 'fa-boxes-stacked', color: 'text-teal-600', bg: 'bg-teal-50 dark:bg-teal-900/20', perm: 'Inventory' },
        { label: 'Pkg Subs', path: '/admin-package-subscriptions', icon: 'fa-file-invoice-dollar', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20', perm: 'Inventory', badge: stats?.pendingSubscriptions || 0 },
        { label: 'Home Settings', path: '/admin-homepage-settings', icon: 'fa-house-chimney-window', color: 'text-indigo-600', bg: 'bg-indigo-50 dark:bg-indigo-900/20', perm: 'Site Visibility' },
        { label: 'Site Visits', path: '/admin-bookings', icon: 'fa-calendar-check', color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/20', perm: 'Site Visit Bookings', badge: stats?.pendingBookings || 0 },
        { label: 'Site Identity', path: '/admin-site-visibility', icon: 'fa-paintbrush', color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20', perm: 'Site Visibility' },
        { label: 'Legal Pages', path: '/admin-legal', icon: 'fa-file-shield', color: 'text-slate-600', bg: 'bg-slate-50 dark:bg-slate-900/20', perm: 'Legal Content' },
    ];

    const quickActions = user?.role === 'sub-admin'
        ? allQuickActions.filter(a => user.permissions?.includes(a.perm))
        : allQuickActions;

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-4">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-2 leading-none">Dashboard</h1>
                    <p className="text-slate-500 font-medium text-sm md:text-base">Welcome back, <span className="text-blue-600 font-bold">{user?.name}</span>. Here's what's happening today.</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <Link to="/admin-users" className="inline-flex items-center gap-3 px-6 py-3.5 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 text-[12px] md:text-[14px] font-bold rounded-md transition-all shadow-xl active:scale-95">
                        <i className="fa-solid fa-users-gear text-sm"></i>
                        Manage Access
                    </Link>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-gray-100 dark:border-slate-800 shadow-sm">
                    <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-[#05144b] font-semibold text-[14px]">Synchronizing System Data...</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {statCards.map((s, i) => (
                            <div key={i} className="group px-4 pb-2 pt-2 bg-white dark:bg-slate-900 rounded-md border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-xl hover:shadow-black/5 transition-all duration-500 hover:-translate-y-0.5 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-24 h-24 bg-gray-50/50 dark:bg-slate-800/20 rounded-none blur-2xl -mr-6 -mt-6 transition-transform group-hover:scale-110 duration-700"></div>
                                <div className="relative flex items-start justify-between">
                                    <div className="space-y-1.5">
                                        <div className="flex flex-col">
                                             <span className="text-[10px] font-bold text-slate-500 mb-0.5 uppercase tracking-wider">{s.label}</span>
                                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tighter tabular-nums leading-none">{s.value}</h3>
                                        </div>
                                    </div>
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg shadow-sm transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 ${s.bg} ${s.color}`}>
                                        <i className={`fa-solid ${s.icon}`}></i>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-white dark:bg-slate-900 rounded-md p-4 md:p-6 border border-gray-100 dark:border-slate-800 shadow-xl relative overflow-hidden">
                         <div className="flex items-center gap-4 mb-0">
                            <div className="w-12 h-12 bg-blue-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-blue-600/20">
                                <i className="fa-solid fa-bolt-lightning text-xl"></i>
                            </div>
                            <div>
                                 <h4 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white leading-none">Quick Actions</h4>
                                <p className="text-sm font-medium text-slate-500 mt-1">Management shortcuts</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
                            {quickActions.map((action, i) => (
                                <Link 
                                    key={i} 
                                    to={action.path} 
                                    onClick={() => handleActionClick(action.path)}
                                    className="group flex flex-col items-center justify-center p-2 transition-all duration-300 hover:-translate-y-1 relative"
                                >
                                    <div className="relative">
                                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-xl mb-1 group-hover:scale-110 transition-transform duration-500 shadow-sm ${action.bg} ${action.color}`}>
                                            <i className={`fa-solid ${action.icon}`}></i>
                                        </div>
                                        {action.badge && !clearedBadges.includes(action.path) ? (
                                            <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-md z-10 border-2 border-white dark:border-slate-900 animate-pulse">
                                                {action.badge}
                                            </span>
                                        ) : null}
                                    </div>
                                     <span className="text-sm font-bold text-slate-700 dark:text-slate-300 group-hover:text-blue-600 transition-colors text-center">{action.label}</span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}



